import {
  Card,
  CardBody,
  CardHeader,
  CardSubtitle,
  CardTitle,
  GridLayout,
  GridLayoutItem
} from '@progress/kendo-react-layout'
import {
  Input,
  InputChangeEvent,
  NumericTextBox,
  NumericTextBoxChangeEvent,
  TextArea,
  TextAreaChangeEvent
} from '@progress/kendo-react-inputs'
import { Contents } from '../../../components/layouts'
import {
  AvailabilityStatusSelect,
  BranchSelect,
  BuildingUsecaseDropDownList,
  ConstrFormSelect,
  ConstrTypeSelect,
  DesignClassificationSelect,
  EvaluationSelect,
  FiscalMonthDropDownList,
  FiscalYearDropDownList,
  ObtainHistorySelect,
  ProcurementStatusSelect,
  StructureTypeSelect,
  UnderGroundConstrMethodSelect,
  EffortLevelSelect,
  PrefectureSelect
} from '../../../components/parts'
import { FC, useState, useEffect, SetStateAction, Dispatch } from 'react'
import { produce } from 'immer'
import {
  BuildingInfoDisabled,
  BuildingInfoPut,
  MaintenanceParams
} from '@gateway.Building'
import { useAppSelector } from '../../../stores'
import { Authority } from '../../../gateway/system/Authority'
import './BuildingMaintenance.css'
import { Tooltip } from '@progress/kendo-react-tooltip'

const BUILDING_INFO_DISABLED: BuildingInfoDisabled = {
  branchCdDisabled: false,
  procurementNoDisabled: false,
  buildingNoDisabled: false,
  evaluationDisabled: false,
  updStsStrDisabled: false,
  updDtmDisabled: false,
  updUserKbnStrDisabled: false,
  updUserNmDisabled: false,
  constructionNmDisabled: false,
  procurementStsStrDisabled: false,
  obtainHistoryCdDisabled: false,
  constructionFormCdDisabled: false,
  ourCompanyEquityRatioDisabled: false,
  obtainFyDisabled: false,
  prefectureDisabled: false,
  cityDisabled: false,
  constructionSubjectCdDisabled: false,
  totalCostDisabled: false,
  ourCompanyTotalCostDisabled: false,
  constructionCdDisabled: false,
  architectureUseCdDisabled: false,
  structureUndergroundCdDisabled: false,
  structureGroundCdDisabled: false,
  totalFloorAreaM2Disabled: false,
  totalFloorAreaTsuboDisabled: false,
  floorUnderGroundDisabled: false,
  floorGroundDisabled: false,
  floorPenthouseDisabled: false,
  buildingServicesWorkFlgDisabled: false,
  pilingDisabled: false,
  seismicIsolationDisabled: false,
  topDownDisabled: false,
  specialConstructionMethodStrDisabled: false,
  startYMDisabled: false,
  completionYMDisabled: false,
  scheduleDisabled: false,
  remarkDisabled: false,
  constructionSectionCdDisabled: false,
  chiefPersonCdDisabled: false,
  chiefPersonContactsDisabled: false,
  hqSalesSectionCdDisabled: false,
  hqSalesPersonCdDisabled: false,
  hqSalesPersonContactsDisabled: false,
  branchSalesSectionCdDisabled: false,
  branchSalesPersonCdDisabled: false,
  branchSalesPersonContactsDisabled: false,
  customerNmDisabled: false,
  collabCustomerNmDisabled: false,
  designKbnNmCdDisabled: false,
  designerNmDisabled: false,
  fixCommentDisabled: false,
  effortLevelCdDisabled: false,
  coreTopFlgDisabled: false,
  choiceAndConcentrationTargetDisabled: false,
  siteAreaM2Disabled: false,
  siteAreaTsuboDisabled: false,
  sagyosyoCdDisabled: false
}

/**
 * 画面タイプ
 */
export enum MaintenancePageType {
  /** 物件編集 */
  EDIT = 0,
  /** 物件分割 */
  SPLIT = 1,
  /** 費目内分割 */
  EXPENSE_SPLIT = 2,
  /** 費目内編集 */
  EXPENSE_EDIT = 3,
  /** 新規登録 */
  REGISTRATION = 4
}

export interface BuildingMaintenanceProps {
  pageType: MaintenancePageType
  params: MaintenanceParams
  buildingInfoPut: BuildingInfoPut
  buildingInfoPutStore: BuildingInfoPut
  setBuildingInfoPut: Dispatch<SetStateAction<BuildingInfoPut>>
  setBuildingInfoPutStore: Dispatch<SetStateAction<BuildingInfoPut>>
  setSignInBtnDisabled: Dispatch<SetStateAction<boolean>>
  setBuildingInfoType?: Dispatch<SetStateAction<string>>
}

export const BuildingMaintenance: FC<BuildingMaintenanceProps> = ({
  pageType,
  params,
  buildingInfoPut,
  buildingInfoPutStore,
  setBuildingInfoPut,
  setBuildingInfoPutStore,
  setSignInBtnDisabled,
  setBuildingInfoType
}) => {
  // ログイン情報の取得
  const userProp = useAppSelector((state) => state.common.userProp)

  const common = useAppSelector((x) => x.common)
  const codeNameEvaluations = common.initialData.codeNames('HY01')
  const codeNameBranches = common.initialData.codeNames('SI01')
  const codeNameUpdSts = common.initialData.codeNames('KO02')
  const codeNameEffortLevel = common.initialData.codeNames('TO02')
  const codeNameDesignKbnNm = common.initialData.codeNames('SE04')

  // 物件情報一覧
  const searchDataStore = useAppSelector(
    (state) => state.buildingsearch.buildingSearchData
  )
  // 山積対象物件一覧
  const simulationDataStore = useAppSelector(
    (state) => state.simulationbuildingsearch.simulationBuildingList
  )

  // 更新ステータス
  const [updStsStr, setUpdStsStr] = useState('')
  // 各項目の編集可否
  const [buildingInfoDisabledState, setBuildingInfoDisabledState] = useState<BuildingInfoDisabled>(BUILDING_INFO_DISABLED)
  // 物件番号の非表示または表示
  const [buildingNoDisplay, setBuildingNoDisplay] = useState(true)

  const systemTime = () => {
    const month = new Date().getMonth() + 1
    const year = new Date().getFullYear()
    const date = month > 9 ? `${year}${month}` : `${year}0${month}`
    return date
  }

  /** 延床面積㎡ 延床面積坪 */
  const handleDispTotalFloorAreaM2AndTsubo = (e: NumericTextBoxChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispTotalFloorAreaM2 = e.target.value ?? 0
        draft.dispTotalFloorAreaTsubo = e.target.value !== null ? Number((e.target.value * 0.3025).toFixed(3)) : 0
      })
    )
  }

  /** 敷地面積㎡ 敷地面積坪 */
  const handleDispSiteAreaM2AndTsubo = (e: NumericTextBoxChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispSiteAreaM2 = e.target.value ?? 0
        draft.dispSiteAreaTsubo = e.target.value !== null ? Number((e.target.value * 0.3025).toFixed(3)) : 0
      })
    )
  }

  /** branchCdList 支店 **/
  const handleBranchCdList = (val: string[]) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.branchCd = val[0]

        const name = codeNameBranches.find(
          (codeName) => codeName.CODE === val[0]
        )?.NAME
        draft.branchNm = name ?? ''
      })
    )
  }

  /** 評価値 */
  const handleEvaluationValue = (val: string[]) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.evaluationValueCd = val[0]

        const name = codeNameEvaluations.find(
          (codeName) => codeName.CODE === val[0]
        )?.NAME
        draft.evaluationValue = name ?? ''
      })
    )
  }

  /** 選択と集中対象 */
  const handleChoiceAndConcentrationTarget = (val: string[]) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispChoiceAndConcentrationTarget = Number(val[0])
      })
    )
  }

  /** 工事名 */
  const handleConstructionNm = (event: InputChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispConstructionNm = event.value
      })
    )
  }

  /** 調達状況 */
  const handleProcurementSts = (val: string[]) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.procurementSts = val[0]
      })
    )
  }

  /** 入手経緯 */
  const handleObtainHistoryCd = (val: string[]) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispObtainHistoryCd = val[0]
      })
    )
  }

  /** 施工形式 */
  const handleConstructionFormCd = (val: string[]) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispConstructionFormCd = val[0]
      })
    )
  }

  /** 当社持分比 */
  const handleOurCompanyEquityRatio = (event: NumericTextBoxChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispOurCompanyEquityRatio = event.value ?? null as unknown as number
      })
    )
  }

  /** 取組レベル */
  const handleEffortLevel = (val: string[]) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispEffortLevelCd = val[0]

        const name = codeNameEffortLevel.find(
          (codeName) => codeName.CODE === val[0]
        )?.NAME
        draft.dispEffortLevel = name ?? ''
      })
    )
  }

  /** コアトップ案件フラグ */
  const handleCoreTopFlg = (val: string[]) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispCoreTopFlg = Number(val[0])
      })
    )
  }

  /** 入手年度 */
  const handleObtainFy = (year: number) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispObtainFY = year.toString()
      })
    )
  }

  /** 都道府県名 */
  const handlePrefecture = (val: string[]) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispPrefecture = val[0]
      })
    )
  }

  /** 市区町村名 */
  const handleCityName = (event: InputChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispCity = event.value
      })
    )
  }

  /** 工事科目コード */
  const handleConstructionSubjectCd = (event: NumericTextBoxChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispConstructionSubjectCd =
          event.value !== null
            ? String(event.value)
            : null as unknown as string
      })
    )
  }

  /** 総工事価格 */
  const handleTotalCost = (event: NumericTextBoxChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispTotalCost = event.value ?? 0
      })
    )
  }

  /** 当社概算工事価格 */
  const handleOurCompanyTotalCost = (event: NumericTextBoxChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispOurCompanyTotalCost = event.value ?? 0
      })
    )
  }

  /** 工事種別 */
  const handleConstructionCd = (val: string[]) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispConstructionCd = val[0]
      })
    )
  }

  /** 建物用途 */
  const handleArchitectureUse = (val: string) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.architectureUse = val
      })
    )
  }

  /** 構造・地下 */
  const handleDispStructureUnderground = (val: string[]) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispStructureUnderGround = val[0]
      })
    )
  }

  /** 構造・地上 */
  const handleDispStructureGround = (val: string[]) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispStructureGround = val[0]
      })
    )
  }

  /** 階数・地下 */
  const handleDispFloorUnderGround = (event: NumericTextBoxChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispFloorUnderGround = event.value
      })
    )
  }

  /** 階数・地上 */
  const handleDispFloorGround = (event: NumericTextBoxChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispFloorGround = event.value
      })
    )
  }

  /** 階数・塔屋 */
  const handleDispFloorPenthouse = (event: NumericTextBoxChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispFloorPenthouse = event.value
      })
    )
  }

  /** 設備工事 */
  const handleDispBuildingServicesWorkFlg = (val: string[]) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispBuildingServicesWorkFlg = Number(val[0])
      })
    )
  }

  /** 杭打 */
  const handlePiling = (val: string[]) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.piling = Number(val[0])
      })
    )
  }

  /**  免震 */
  const handleSeismicIsolation = (val: string[]) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.seismicIsolation = Number(val[0])
      })
    )
  }

  /**  順打逆打 */
  const handleTopDown = (val: string[]) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.topDownFlg = Number(val[0])
      })
    )
  }

  /** 工法 */
  const handleSpecialConstructionMethodCd = (event: InputChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispSpecialConstructionMethod = event.value
      })
    )
  }

  /** 着工年 */
  const handleStartY = (year: number) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.startY = year.toString()
      })
    )
  }

  /** 着工月 */
  const handleStartM = (month: number) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.startM = month.toString()
      })
    )
  }

  /** 竣工年 */
  const handleCompletionY = (year: number) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.completionY = year.toString()
      })
    )
  }

  /** 竣工月 */
  const handleCompletionM = (month: number) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.completionM = month.toString()
      })
    )
  }

  /**  工期 */
  const handleSchedule = (event: NumericTextBoxChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispSchedule = event.value
      })
    )
  }

  /** 備考 */
  const handleRemark = (event: TextAreaChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.remark = event.value
      })
    )
  }

  /** 本社営業部名 */
  const handleHqSalesSectionNm = (event: InputChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispHqSalesSectionNm = event.value
      })
    )
  }

  /** 本社営業担当者名 */
  const handleHqSalesPersonNm = (event: InputChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispHqSalesPersonNm = event.value
      })
    )
  }

  /** 本社営業担当者連絡先 */
  const handleHqSalesPersonContacts = (event: InputChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispHqSalesPersonContacts = event.value
      })
    )
  }

  /** 支店営業部名 */
  const handleBranchSalesSectionNm = (event: InputChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispBranchSalesSectionNm = event.value
      })
    )
  }

  /** 支店営業担当者名 */
  const handleBranchSalesPersonNm = (event: InputChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispBranchSalesPersonNm = event.value
      })
    )
  }

  /** 支店営業担当者連絡先 */
  const handleBranchSalesPersonContacts = (event: InputChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispBranchSalesPersonContacts = event.value
      })
    )
  }

  /** 発注者名 */
  const handleCustomerNm = (event: InputChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispCustomerNm = event.value
      })
    )
  }

  /** 共同発注者名 */
  const handleCollabCustomerNm = (event: InputChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispCollabCustomerNm = event.value
      })
    )
  }

  /** 設計区分 */
  const handleDesignKbnNm = (val: string[]) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispDesignKbn = val[0]

        const name = codeNameDesignKbnNm.find(
          (codeName) => codeName.CODE === val[0]
        )?.NAME
        draft.dispDesignKbnNm = name ?? ''
      })
    )
  }

  /** 設計者 */
  const handleInputDesignerNm = (event: InputChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispDesignerNm = event.value
      })
    )
  }

  /** 修正コメント */
  const handleFixComment = (event: TextAreaChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.fixComment = event.value
      })
    )
  }

  /** 工事部名 */
  const handleConstructionSectionNm = (event: InputChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispConstructionSectionNm = event.value
      })
    )
  }

  /** 所長担当者名 */
  const handleChiefPersonNm = (event: InputChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispChiefPersonNm = event.value
      })
    )
  }

  /** 所長担当者連絡先 */
  const handleChiefPersonContacts = (event: InputChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispChiefPersonContacts = event.value
      })
    )
  }

  /** 作業所コード */
  const handleSagyosyoCd = (event: NumericTextBoxChangeEvent) => {
    setBuildingInfoPut(
      produce((draft) => {
        draft.dispSagyosyoCd =
          event.value !== null
            ? String(event.value)
            : null as unknown as string
      })
    )
  }

  /**
   * すべて項目を編集不可に設定する
   * @returns BuildingInfoDisabled
   */
  const changeBuildingInfoDisabledStateToTrue = () => {
    const buildingInfoDisabled = Object.assign({}, BUILDING_INFO_DISABLED)
    buildingInfoDisabled.branchCdDisabled = true
    buildingInfoDisabled.procurementNoDisabled = true
    buildingInfoDisabled.buildingNoDisabled = true
    buildingInfoDisabled.evaluationDisabled = true
    buildingInfoDisabled.updStsStrDisabled = true
    buildingInfoDisabled.updDtmDisabled = true
    buildingInfoDisabled.updUserKbnStrDisabled = true
    buildingInfoDisabled.updUserNmDisabled = true
    buildingInfoDisabled.constructionNmDisabled = true
    buildingInfoDisabled.procurementStsStrDisabled = true
    buildingInfoDisabled.obtainHistoryCdDisabled = true
    buildingInfoDisabled.constructionFormCdDisabled = true
    buildingInfoDisabled.ourCompanyEquityRatioDisabled = true
    buildingInfoDisabled.obtainFyDisabled = true
    buildingInfoDisabled.prefectureDisabled = true
    buildingInfoDisabled.cityDisabled = true
    buildingInfoDisabled.constructionSubjectCdDisabled = true
    buildingInfoDisabled.totalCostDisabled = true
    buildingInfoDisabled.ourCompanyTotalCostDisabled = true
    buildingInfoDisabled.constructionCdDisabled = true
    buildingInfoDisabled.architectureUseCdDisabled = true
    buildingInfoDisabled.structureUndergroundCdDisabled = true
    buildingInfoDisabled.structureGroundCdDisabled = true
    buildingInfoDisabled.totalFloorAreaM2Disabled = true
    buildingInfoDisabled.totalFloorAreaTsuboDisabled = true
    buildingInfoDisabled.floorUnderGroundDisabled = true
    buildingInfoDisabled.floorGroundDisabled = true
    buildingInfoDisabled.floorPenthouseDisabled = true
    buildingInfoDisabled.buildingServicesWorkFlgDisabled = true
    buildingInfoDisabled.pilingDisabled = true
    buildingInfoDisabled.seismicIsolationDisabled = true
    buildingInfoDisabled.topDownDisabled = true
    buildingInfoDisabled.specialConstructionMethodStrDisabled = true
    buildingInfoDisabled.startYMDisabled = true
    buildingInfoDisabled.completionYMDisabled = true
    buildingInfoDisabled.scheduleDisabled = true
    buildingInfoDisabled.remarkDisabled = true
    buildingInfoDisabled.constructionSectionCdDisabled = true
    buildingInfoDisabled.chiefPersonCdDisabled = true
    buildingInfoDisabled.chiefPersonContactsDisabled = true
    buildingInfoDisabled.hqSalesSectionCdDisabled = true
    buildingInfoDisabled.hqSalesPersonCdDisabled = true
    buildingInfoDisabled.hqSalesPersonContactsDisabled = true
    buildingInfoDisabled.branchSalesSectionCdDisabled = true
    buildingInfoDisabled.branchSalesPersonCdDisabled = true
    buildingInfoDisabled.branchSalesPersonContactsDisabled = true
    buildingInfoDisabled.customerNmDisabled = true
    buildingInfoDisabled.collabCustomerNmDisabled = true
    buildingInfoDisabled.designKbnNmCdDisabled = true
    buildingInfoDisabled.designerNmDisabled = true
    buildingInfoDisabled.fixCommentDisabled = true
    buildingInfoDisabled.effortLevelCdDisabled = true
    buildingInfoDisabled.coreTopFlgDisabled = true
    buildingInfoDisabled.choiceAndConcentrationTargetDisabled = true
    buildingInfoDisabled.siteAreaM2Disabled = true
    buildingInfoDisabled.siteAreaTsuboDisabled = true
    buildingInfoDisabled.sagyosyoCdDisabled = true

    return buildingInfoDisabled
  }

  // 画面タイプ＝物件編集の場合
  useEffect(() => {
    if (pageType === MaintenancePageType.EDIT) {
      for (let i = 0; i < searchDataStore.buildingInfoList.length; i++) {
        const element = searchDataStore.buildingInfoList[i]
        if (
          element.buildingInfo.procurementNo === params.procurementNo &&
          element.buildingInfo.subNo === params.subNo &&
          element.buildingInfo.branchCd === params.branchCd
        ) {
          const buildingInfo = element.buildingInfo
          const putObj = {
            ...element.buildingInfo,
            /** 着工年 */
            startY: buildingInfo.dispStartYM?.substring(0, 4) ?? '',
            /** 着工月 */
            startM: buildingInfo.dispStartYM?.substring(4, 6) ?? '',
            /** 竣工年 */
            completionY: buildingInfo.dispCompletionYM?.substring(0, 4) ?? '',
            /** 竣工月 */
            completionM: buildingInfo.dispCompletionYM?.substring(4, 6) ?? '',
            // 初期値は円単位を百万単位に変換する
            /** 総工事価格 */
            totalCost:
              buildingInfo.totalCost !== null && buildingInfo.totalCost !== undefined && buildingInfo.totalCost !== 0
                ? buildingInfo.totalCost / 1000000
                : buildingInfo.totalCost,
            /** 当社概算工事価格 */
            ourCompanyTotalCost:
              buildingInfo.ourCompanyTotalCost !== null && buildingInfo.ourCompanyTotalCost !== undefined && buildingInfo.ourCompanyTotalCost !== 0
                ? buildingInfo.ourCompanyTotalCost / 1000000
                : buildingInfo.ourCompanyTotalCost,
            /** 表示用総工事価格 */
            dispTotalCost:
                buildingInfo.dispTotalCost !== null && buildingInfo.dispTotalCost !== undefined && buildingInfo.dispTotalCost !== 0
                  ? buildingInfo.dispTotalCost / 1000000
                  : buildingInfo.dispTotalCost,
            /** 表示用当社概算工事価格 */
            dispOurCompanyTotalCost:
                buildingInfo.dispOurCompanyTotalCost !== null && buildingInfo.dispOurCompanyTotalCost !== undefined && buildingInfo.dispOurCompanyTotalCost !== 0
                  ? buildingInfo.dispOurCompanyTotalCost / 1000000
                  : buildingInfo.dispOurCompanyTotalCost,
            /** シミュレーション年月度 */
            simulationYm: null,
            /** 費目 */
            expense: null,
            /** 費目連番 */
            expenseSeq: null,
            /** モード区分 */
            modeKbn: null
          }
          setBuildingInfoPut(putObj)
          setBuildingInfoPutStore(putObj)
          // 更新ステータス名称
          setUpdStsStr(buildingInfo.updStsStr ?? '')

          break
        }
      }
    }
  }, [
    pageType,
    params.branchCd,
    params.procurementNo,
    params.subNo,
    searchDataStore.buildingInfoList,
    setBuildingInfoPut
  ])

  // 画面タイプ＝費目分割編集の場合
  useEffect(() => {
    if (pageType === MaintenancePageType.EXPENSE_EDIT) {
      for (let i = 0; i < simulationDataStore.length; i++) {
        const element = simulationDataStore[i]
        if (
          element.procurementNo === params.procurementNo &&
          element.subNo === params.subNo &&
          element.branchCd === params.branchCd &&
          element.expense === params.expenseCode &&
          element.expenseSeq === params.expenseSeq
        ) {
          const buildingInfo = element
          const putObj = {
            ...element,
            /** 着工年 */
            startY: element.dispStartYM?.substring(0, 4) ?? '',
            /** 着工月 */
            startM: element.dispStartYM?.substring(4, 6) ?? '',
            /** 竣工年 */
            completionY: buildingInfo.dispCompletionYM?.substring(0, 4) ?? '',
            /** 竣工月 */
            completionM: buildingInfo.dispCompletionYM?.substring(4, 6) ?? '',
            // 初期値は円単位を百万単位に変換する
            /** 総工事価格 */
            totalCost:
              buildingInfo.totalCost !== null && buildingInfo.totalCost !== undefined && buildingInfo.totalCost !== 0
                ? buildingInfo.totalCost / 1000000
                : buildingInfo.totalCost,
            /** 当社概算工事価格 */
            ourCompanyTotalCost:
              buildingInfo.ourCompanyTotalCost !== null && buildingInfo.ourCompanyTotalCost !== undefined && buildingInfo.ourCompanyTotalCost !== 0
                ? buildingInfo.ourCompanyTotalCost / 1000000
                : buildingInfo.ourCompanyTotalCost,
            /** 総工事価格 */
            dispTotalCost:
                buildingInfo.dispTotalCost !== null && buildingInfo.dispTotalCost !== undefined && buildingInfo.dispTotalCost !== 0
                  ? buildingInfo.dispTotalCost / 1000000
                  : buildingInfo.dispTotalCost,
            /** 当社概算工事価格 */
            dispOurCompanyTotalCost:
                buildingInfo.dispOurCompanyTotalCost !== null && buildingInfo.dispOurCompanyTotalCost !== undefined && buildingInfo.dispOurCompanyTotalCost !== 0
                  ? buildingInfo.dispOurCompanyTotalCost / 1000000
                  : buildingInfo.dispOurCompanyTotalCost,
            simulationYm: systemTime(),
            buildingNo: '',
            updSts: codeNameUpdSts.find((v) => v.CODE === buildingInfo.updSts)?.NAME ?? ''
          }
          setBuildingInfoPut(putObj)
          setBuildingInfoPutStore(putObj)

          // 更新ステータス
          setUpdStsStr(codeNameUpdSts.find(v => v.CODE === buildingInfo.updSts)?.NAME ?? '')

          break
        }
      }
    }
  }, [
    pageType,
    params.branchCd,
    params.expenseCode,
    params.expenseSeq,
    params.procurementNo,
    params.subNo,
    setBuildingInfoPut,
    simulationDataStore
  ])

  useEffect(() => {
    // 物件情報タイプの決定方法
    let buildingInfoType = ''
    if (buildingInfoPut.addUserKbn === '1') {
      // 物件情報.枝番（子）='00'
      if (buildingInfoPut.subNo === '00') {
        buildingInfoType = '取込物件'
      }
    } else if (buildingInfoPut.addUserKbn === '2') {
      if (buildingInfoPut.subNo === '00') {
        buildingInfoType = '工務登録物件'
      } else {
        buildingInfoType = '工務分割子物件'
      }
    } else if (buildingInfoPut.addUserKbn === '3') {
      if (buildingInfoPut.subNo === '00') {
        buildingInfoType = '建材部登録物件'
      }
    } else if (buildingInfoPut.addUserKbn === '4') {
      if (buildingInfoPut.subNo === '00') {
        buildingInfoType = '支店登録物件'
      } else {
        buildingInfoType = '支店分割子物件'
      }
    }

    setBuildingInfoType?.(buildingInfoType)

    // 画面タイプ＝物件編集
    if (pageType === MaintenancePageType.EDIT) {
      // ログイン情報．利用者権限＝工務（コード＝2）の場合
      if (userProp?.authority === Authority.Builder) {
        if (buildingInfoType === '取込物件') {
          // 支店以外を編集可に設定する
          const buildingInfoDisabled = Object.assign({}, BUILDING_INFO_DISABLED)
          buildingInfoDisabled.branchCdDisabled = true

          setBuildingInfoDisabledState(buildingInfoDisabled)

          // 物件番号を表示
          setBuildingNoDisplay(true)
        }
        if (
          buildingInfoType === '工務分割子物件' ||
          buildingInfoType === '工務登録物件' ||
          buildingInfoType === '建材部登録物件' ||
          buildingInfoType === '支店登録物件' ||
          buildingInfoType === '支店分割子物件'
        ) {
          // 画面タイプ、物件タイプ、利用者権限に応じて各項目の編集可否が決定する
          setBuildingInfoDisabledState(
            produce((draft) => {
              draft.procurementNoDisabled = true
              draft.updStsStrDisabled = true
              draft.updDtmDisabled = true
              draft.updUserKbnStrDisabled = true
              draft.updUserNmDisabled = true
              draft.totalFloorAreaTsuboDisabled = true
            })
          )
          // 物件番号を非表示
          setBuildingNoDisplay(false)
        }
        // 登録ボタンを活性に設定する
        setSignInBtnDisabled(false)
      }

      // 利用者権限＝建材部 建材部公開フラグ＝０（建材部未公開）の物件
      if (
        userProp?.authority === Authority.Material &&
        buildingInfoPutStore.kenzaiDiscloseFlg === 0
      ) {
        if (
          buildingInfoType === '取込物件' ||
          buildingInfoType === '工務分割子物件' ||
          buildingInfoType === '工務登録物件' ||
          buildingInfoType === '支店登録物件' ||
          buildingInfoType === '支店分割子物件'
        ) {
          // すべて項目を編集不可に設定する
          const buildingInfoDisabled = changeBuildingInfoDisabledStateToTrue()
          setBuildingInfoDisabledState(buildingInfoDisabled)

          // 物件番号を表示
          setBuildingNoDisplay(true)
          // 登録ボタンを非活性に設定する
          setSignInBtnDisabled(true)
        }
        if (buildingInfoType === '建材部登録物件') {
          // 画面タイプ、物件タイプ、利用者権限に応じて各項目の編集可否が決定する
          setBuildingInfoDisabledState(
            produce((draft) => {
              draft.procurementNoDisabled = true
              draft.updStsStrDisabled = true
              draft.updDtmDisabled = true
              draft.updUserKbnStrDisabled = true
              draft.updUserNmDisabled = true
              draft.totalFloorAreaTsuboDisabled = true
            })
          )
          // 物件番号を非表示
          setBuildingNoDisplay(false)
          // 登録ボタンを活性に設定する
          setSignInBtnDisabled(false)
        }
      }

      // 利用者権限＝建材部 建材部公開フラグ＝１（建材部公開済み）の物件
      if (
        userProp?.authority === Authority.Material &&
        buildingInfoPutStore.kenzaiDiscloseFlg === 1
      ) {
        if (
          buildingInfoType === '取込物件' ||
          buildingInfoType === '工務分割子物件' ||
          buildingInfoType === '工務登録物件' ||
          buildingInfoType === '支店登録物件' ||
          buildingInfoType === '支店分割子物件'
        ) {
          // すべて項目を編集不可に設定する
          const buildingInfoDisabled = changeBuildingInfoDisabledStateToTrue()
          buildingInfoDisabled.specialConstructionMethodStrDisabled = false
          buildingInfoDisabled.remarkDisabled = false
          buildingInfoDisabled.fixCommentDisabled = false

          setBuildingInfoDisabledState(buildingInfoDisabled)

          // 物件番号を表示
          setBuildingNoDisplay(true)
        }
        if (buildingInfoType === '建材部登録物件') {
          // 画面タイプ、物件タイプ、利用者権限に応じて各項目の編集可否が決定する
          setBuildingInfoDisabledState(
            produce((draft) => {
              draft.procurementNoDisabled = true
              draft.updStsStrDisabled = true
              draft.updDtmDisabled = true
              draft.updUserKbnStrDisabled = true
              draft.updUserNmDisabled = true
              draft.totalFloorAreaTsuboDisabled = true
            })
          )
          // 物件番号を非表示
          setBuildingNoDisplay(false)
        }
        // 登録ボタンを活性に設定する
        setSignInBtnDisabled(false)
      }

      // 利用者権限＝支店 かつ ログイン情報．支店コード≠対象の物件情報．支店コード
      if (
        userProp?.authority === Authority.Branch &&
        userProp?.branchCode !== buildingInfoPutStore.branchCd
      ) {
        // すべて項目を編集不可に設定する
        const buildingInfoDisabled = changeBuildingInfoDisabledStateToTrue()
        setBuildingInfoDisabledState(buildingInfoDisabled)
        // 物件番号を表示
        setBuildingNoDisplay(true)
        // 登録ボタンを非活性に設定する
        setSignInBtnDisabled(true)
      }

      // 利用者権限＝支店 かつ ログイン情報．支店コード=対象の物件情報．支店コード
      if (
        userProp?.authority === Authority.Branch &&
        userProp?.branchCode === buildingInfoPutStore.branchCd
      ) {
        if (buildingInfoType === '取込物件') {
          // すべて項目を編集不可に設定する
          const buildingInfoDisabled = changeBuildingInfoDisabledStateToTrue()

          setBuildingInfoDisabledState(buildingInfoDisabled)
          // 物件番号を表示
          setBuildingNoDisplay(true)
          // 登録ボタンを非活性に設定する
          setSignInBtnDisabled(true)
        }

        if (
          buildingInfoType === '工務分割子物件' ||
          buildingInfoType === '工務登録物件' ||
          buildingInfoType === '建材部登録物件'
        ) {
          // すべて項目を編集不可に設定する
          const buildingInfoDisabled = changeBuildingInfoDisabledStateToTrue()
          setBuildingInfoDisabledState(buildingInfoDisabled)
          // 物件番号を表示
          setBuildingNoDisplay(true)
          // 登録ボタンを非活性に設定する
          setSignInBtnDisabled(true)
        }

        if (
          buildingInfoType === '支店登録物件' ||
          buildingInfoType === '支店分割子物件'
        ) {
          setBuildingInfoDisabledState(
            produce((draft) => {
              draft.procurementNoDisabled = true
              draft.updStsStrDisabled = true
              draft.updDtmDisabled = true
              draft.updUserKbnStrDisabled = true
              draft.updUserNmDisabled = true
              draft.totalFloorAreaTsuboDisabled = true
            })
          )

          // 物件番号を非表示
          setBuildingNoDisplay(false)
          // 登録ボタンを活性に設定する
          setSignInBtnDisabled(false)
        }
      }
    }

    // 画面タイプ＝費目内編集
    if (pageType === MaintenancePageType.EXPENSE_EDIT) {
      // 画面タイプ＝費目内編集、利用者権限＝建材部 または 利用者権限＝工務部 または 利用者権限=支店かつログイン情報．支店コード＝対象の物件情報．支店コード
      if (
        userProp?.authority === Authority.Material ||
        userProp?.authority === Authority.Builder ||
        (userProp?.authority === Authority.Branch &&
          userProp?.branchCode === buildingInfoPutStore.branchCd)
      ) {
        setBuildingInfoDisabledState(
          produce((draft) => {
            draft.procurementNoDisabled = true
            draft.updStsStrDisabled = true
            draft.updDtmDisabled = true
            draft.updUserKbnStrDisabled = true
            draft.updUserNmDisabled = true
            draft.totalFloorAreaTsuboDisabled = true
          })
        )

        // 物件番号を非表示
        setBuildingNoDisplay(false)
        // 登録ボタンを活性に設定する
        setSignInBtnDisabled(false)
      }

      // 利用者権限=支店かつログイン情報．支店コード≠対象の物件情報．支店コード
      if (
        userProp?.authority === Authority.Branch &&
        userProp?.branchCode !== buildingInfoPutStore.branchCd
      ) {
        // すべて項目を編集不可に設定する
        const buildingInfoDisabled = changeBuildingInfoDisabledStateToTrue()
        setBuildingInfoDisabledState(buildingInfoDisabled)
        // 物件番号を表示
        setBuildingNoDisplay(true)
        // 登録ボタンを非活性に設定する
        setSignInBtnDisabled(true)
      }
    }

    // 工務担当者/建材部担当者/支店担当者以外の権限場合、利用可・更新権限がありません
    if (
      userProp?.authority !== Authority.Builder &&
      userProp?.authority !== Authority.Material &&
      userProp?.authority !== Authority.Branch
    ) {
      // 登録ボタンを非活性に設定する
      setSignInBtnDisabled(true)
    }
  }, [
    buildingInfoPut,
    buildingInfoPutStore,
    pageType,
    userProp?.authority,
    userProp?.branchCode
  ])

  // 工期計算
  useEffect(() => {
    if (buildingInfoPut.completionY !== '' && buildingInfoPut.completionM !== '' && buildingInfoPut.startY !== '' && buildingInfoPut.startM !== '') {
      const monthesC = Number(buildingInfoPut.completionY ?? 0) * 12 + (Number(buildingInfoPut.completionM ?? 0) - 1)
      const monthesS = Number(buildingInfoPut.startY ?? 0) * 12 + (Number(buildingInfoPut.startM ?? 0) - 1)
      const diff = monthesC - monthesS + 1
      setBuildingInfoPut(
        produce((draft) => {
          draft.dispSchedule = diff
        })
      )
    }
  }, [buildingInfoPut.completionY, buildingInfoPut.completionM, buildingInfoPut.startY, buildingInfoPut.startM])

  const procurementNo = pageType === MaintenancePageType.EXPENSE_EDIT
    ? `${buildingInfoPutStore.procurementNo ?? ''}-${buildingInfoPutStore.subNo ?? ''}-${buildingInfoPutStore.expense ?? ''}-${buildingInfoPutStore.expenseSeq ?? ''}`
    : `${buildingInfoPutStore.procurementNo ?? ''}-${buildingInfoPutStore.subNo ?? ''}`

  return (
    <Contents>
      <>
        <Tooltip position={'right'} parentTitle={true} className={'pcy-tooltip'}>
          <Card orientation="horizontal">
            <CardHeader style={{ padding: '9.5px 6px', width: '16%' }}>
              <CardTitle style={{ marginTop: '0px' }}>
                <BranchSelect
                  disabled={buildingInfoDisabledState.branchCdDisabled}
                  onChange={handleBranchCdList}
                  style={{
                    fontWeight: 'bold',
                    fontSize: '1.5em',
                    width: '70%'
                  }}
                  value={[buildingInfoPut.branchCd ?? '']}
                  popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                  clear={true}
                  title={'支店（入力必須）'}
                  className={'pcy-disabled-field'}
                />
              </CardTitle>
              <CardSubtitle style={{ marginTop: '0px' }}>
                <span
                  style={{
                    fontSize: '1.5em',
                    fontWeight: 'bold',
                    color: 'gray'
                  }}
                  title={'調達番号'}
                >
                  {procurementNo}
                </span>
              </CardSubtitle>
              {buildingNoDisplay && (
                <CardSubtitle style={{ marginTop: '0px' }}>
                  <span
                    style={{
                      fontSize: '1.5em',
                      fontWeight: 'bold',
                      color: 'gray'
                    }}
                    title={'物件番号'}
                  >
                    {buildingInfoPut.buildingNo}
                  </span>
                </CardSubtitle>
              )}
              <CardSubtitle style={{ marginTop: '0px' }}>
                <EvaluationSelect
                  disabled={buildingInfoDisabledState.evaluationDisabled}
                  onChange={handleEvaluationValue}
                  style={{
                    fontSize: '1.25em',
                    fontWeight: 'bold',
                    width: '70%'
                  }}
                  value={[buildingInfoPut.evaluationValueCd ?? '']}
                  popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                  clear={true}
                  title={'評価値（入力必須）'}
                  className={'pcy-disabled-field'}
                />
              </CardSubtitle>
              <CardSubtitle style={{ marginTop: '0px' }}>
                <span style={{ fontWeight: 'bold', fontSize: '1em' }}>
                  選択と集中:
                </span>
                <AvailabilityStatusSelect
                  disabled={buildingInfoDisabledState.choiceAndConcentrationTargetDisabled}
                  onChange={handleChoiceAndConcentrationTarget}
                  style={{
                    fontSize: '1.25em',
                    fontWeight: 'bold',
                    width: '35%'
                  }}
                  value={
                    buildingInfoPut.dispChoiceAndConcentrationTarget !== undefined
                      ? [String(buildingInfoPut.dispChoiceAndConcentrationTarget)]
                      : undefined
                  }
                  popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                  clear={true}
                  title={'選択と集中'}
                  className={'pcy-disabled-field'}
                />
              </CardSubtitle>
              <CardSubtitle style={{ marginTop: '0px' }}>
                <span
                  style={{
                    fontSize: '1.25em',
                    fontWeight: 'bold',
                    color: 'red'
                  }}
                  title={'更新ステータス'}
                >
                  {updStsStr}
                </span>
              </CardSubtitle>
            </CardHeader>
            <CardBody style={{ padding: '9.5px 6px' }}>
              <GridLayout cols={[{ width: '73%' }, { width: '27%' }]}>
                <GridLayoutItem
                  col={1}
                  style={{ marginLeft: 10, marginRight: 10 }}
                >
                  <GridLayout>
                    <GridLayoutItem col={1} colSpan={3} row={1}>
                      <Input
                        disabled={buildingInfoDisabledState.constructionNmDisabled}
                        onChange={handleConstructionNm}
                        style={{ fontWeight: 'bold', fontSize: '1.0em' }}
                        value={buildingInfoPut.dispConstructionNm ?? ''}
                        type={'text'}
                        title={'工事名（入力必須）'}
                        placeholder={'工事名（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={1} row={2}>
                      <NumericTextBox
                        disabled={buildingInfoDisabledState.constructionSubjectCdDisabled}
                        onChange={handleConstructionSubjectCd}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '11%',
                          height: '80%'
                        }}
                        value={
                          buildingInfoPut.dispConstructionSubjectCd !== null
                            ? Number(buildingInfoPut.dispConstructionSubjectCd)
                            : null
                        }
                        max={999999}
                        format={'#'}
                        spinners={false}
                        title={'工事科目コード'}
                        placeholder={'工事科目コード'}
                        className={'pcy-disabled-field'}
                      />
                      <ProcurementStatusSelect
                        disabled={buildingInfoDisabledState.procurementStsStrDisabled}
                        onChange={handleProcurementSts}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '12%',
                          height: '80%'
                        }}
                        value={[buildingInfoPut.procurementSts ?? '']}
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'調達状況'}
                        className={'pcy-disabled-field'}
                      />
                      <ObtainHistorySelect
                        disabled={buildingInfoDisabledState.obtainHistoryCdDisabled}
                        onChange={handleObtainHistoryCd}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '10%',
                          height: '80%'
                        }}
                        value={[buildingInfoPut.dispObtainHistoryCd ?? '']}
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'入手経緯'}
                        className={'pcy-disabled-field'}
                      />
                      <ConstrFormSelect
                        disabled={buildingInfoDisabledState.constructionFormCdDisabled}
                        onChange={handleConstructionFormCd}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '18%',
                          height: '80%'
                        }}
                        value={[buildingInfoPut.dispConstructionFormCd ?? '']}
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'施工形式'}
                        className={'pcy-disabled-field'}
                      />
                      <NumericTextBox
                        disabled={buildingInfoDisabledState.ourCompanyEquityRatioDisabled}
                        onChange={handleOurCompanyEquityRatio}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '9%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispOurCompanyEquityRatio}
                        max={100}
                        spinners={false}
                        title={'当社持分比'}
                        placeholder={'当社持分比'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em', width: '8%' }}>
                        %
                      </span>
                      <EffortLevelSelect
                        disabled={buildingInfoDisabledState.effortLevelCdDisabled}
                        onChange={handleEffortLevel}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '15%',
                          height: '80%'
                        }}
                        value={[buildingInfoPut.dispEffortLevelCd ?? '']}
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'取組レベル'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em', width: '8%' }}>
                        コアトップ案件:
                      </span>
                      <AvailabilityStatusSelect
                        disabled={buildingInfoDisabledState.coreTopFlgDisabled}
                        onChange={handleCoreTopFlg}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '8%',
                          height: '80%'
                        }}
                        value={
                          buildingInfoPut.dispCoreTopFlg !== undefined
                            ? [String(buildingInfoPut.dispCoreTopFlg)]
                            : undefined
                        }
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={1} row={3}>
                      <FiscalYearDropDownList
                        disabled={buildingInfoDisabledState.obtainFyDisabled}
                        onYearChange={handleObtainFy}
                        fyddlStyle={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '10%',
                          height: '80%'
                        }}
                        origin={-5}
                        range={16}
                        fyddlValue={
                          buildingInfoPut.dispObtainFY !== null && buildingInfoPut.dispObtainFY !== ''
                            ? Number(buildingInfoPut.dispObtainFY)
                            : undefined
                        }
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        fyTitle={'入手年度（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <PrefectureSelect
                        disabled={buildingInfoDisabledState.prefectureDisabled}
                        onChange={handlePrefecture}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '12%',
                          height: '80%'
                        }}
                        value={[buildingInfoPut.dispPrefecture ?? '']}
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'都道府県'}
                        className={'pcy-disabled-field'}
                      />
                      <Input
                        disabled={buildingInfoDisabledState.cityDisabled}
                        onChange={handleCityName}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '9%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispCity ?? ''}
                        type={'text'}
                        title={'市区町村'}
                        placeholder={'市区町村'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em', paddingLeft: '2%' }}>
                        総工事金額（百万）:￥
                      </span>
                      <NumericTextBox
                        disabled={buildingInfoDisabledState.totalCostDisabled}
                        onChange={handleTotalCost}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '10%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispTotalCost}
                        spinners={false}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em', paddingLeft: '2%' }}>
                        当社概算工事金額（百万）:￥
                      </span>
                      <NumericTextBox
                        disabled={buildingInfoDisabledState.ourCompanyTotalCostDisabled}
                        onChange={handleOurCompanyTotalCost}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '10%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispOurCompanyTotalCost}
                        spinners={false}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={1} row={4}>
                      <ConstrTypeSelect
                        disabled={buildingInfoDisabledState.constructionCdDisabled}
                        onChange={handleConstructionCd}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '10%',
                          height: '80%'
                        }}
                        value={[buildingInfoPut.dispConstructionCd ?? '']}
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'工事種別'}
                        className={'pcy-disabled-field'}
                      />
                      <BuildingUsecaseDropDownList
                        disabled={buildingInfoDisabledState.architectureUseCdDisabled}
                        onChange={handleArchitectureUse}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '18%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.architectureUse ?? ''}
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'建物用途（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        （
                      </span>
                      <StructureTypeSelect
                        disabled={buildingInfoDisabledState.structureUndergroundCdDisabled}
                        onChange={handleDispStructureUnderground}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '13%',
                          height: '80%'
                        }}
                        value={[buildingInfoPut.dispStructureUnderGround ?? '']}
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'構造・地下（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        /
                      </span>
                      <StructureTypeSelect
                        disabled={buildingInfoDisabledState.structureGroundCdDisabled}
                        onChange={handleDispStructureGround}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '13%',
                          height: '80%'
                        }}
                        value={[buildingInfoPut.dispStructureGround ?? '']}
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'構造・地上（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        ）
                      </span>
                      <NumericTextBox
                        disabled={buildingInfoDisabledState.totalFloorAreaM2Disabled}
                        onChange={(e) => handleDispTotalFloorAreaM2AndTsubo(e)}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '10%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispTotalFloorAreaM2}
                        spinners={false}
                        title={'延床面積平米（入力必須）'}
                        placeholder={'延床面積平米（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        ㎡ /
                      </span>
                      <NumericTextBox
                        readOnly
                        disabled={true}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '10%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispTotalFloorAreaTsubo}
                        spinners={false}
                        title={'延床面積坪（入力必須）'}
                        placeholder={'延床面積坪（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        坪 -（
                      </span>
                      <NumericTextBox
                        disabled={buildingInfoDisabledState.floorUnderGroundDisabled}
                        onChange={handleDispFloorUnderGround}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '4%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispFloorUnderGround}
                        spinners={false}
                        title={'階数・地下（入力必須）'}
                        placeholder={'階数・地下（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        /
                      </span>
                      <NumericTextBox
                        disabled={buildingInfoDisabledState.floorGroundDisabled}
                        onChange={handleDispFloorGround}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '4%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispFloorGround}
                        spinners={false}
                        title={'階数・地上（入力必須）'}
                        placeholder={'階数・地上（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        /
                      </span>
                      <NumericTextBox
                        disabled={buildingInfoDisabledState.floorPenthouseDisabled}
                        onChange={handleDispFloorPenthouse}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '4%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispFloorPenthouse}
                        spinners={false}
                        title={'階数・塔屋（入力必須）'}
                        placeholder={'階数・塔屋（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                    <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                      ）
                    </span>
                  </GridLayoutItem>
                    <GridLayoutItem col={1} row={5}>
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        設備工事:
                      </span>
                      <AvailabilityStatusSelect
                        disabled={buildingInfoDisabledState.buildingServicesWorkFlgDisabled}
                        onChange={handleDispBuildingServicesWorkFlg}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '9.25%',
                          height: '80%'
                        }}
                        value={
                          buildingInfoPut.dispBuildingServicesWorkFlg !==
                          undefined
                            ? [
                                String(
                                  buildingInfoPut.dispBuildingServicesWorkFlg
                                )
                              ]
                            : undefined
                        }
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'設備工事'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        杭打:
                      </span>
                      <AvailabilityStatusSelect
                        disabled={buildingInfoDisabledState.pilingDisabled}
                        onChange={handlePiling}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '9.25%',
                          height: '80%'
                        }}
                        value={
                          buildingInfoPut.piling !== undefined
                            ? [String(buildingInfoPut.piling)]
                            : undefined
                        }
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'杭打（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        {' '}
                        免震:
                      </span>
                      <AvailabilityStatusSelect
                        disabled={buildingInfoDisabledState.seismicIsolationDisabled}
                        onChange={handleSeismicIsolation}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '9.25%',
                          height: '80%'
                        }}
                        value={
                          buildingInfoPut.seismicIsolation !== undefined
                            ? [String(buildingInfoPut.seismicIsolation)]
                            : undefined
                        }
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'免震（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        {' '}
                      </span>
                      <UnderGroundConstrMethodSelect
                        disabled={buildingInfoDisabledState.topDownDisabled}
                        onChange={handleTopDown}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '12.75%',
                          height: '80%'
                        }}
                        value={
                          buildingInfoPut.topDownFlg !== undefined
                            ? [String(buildingInfoPut.topDownFlg)]
                            : undefined
                        }
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        title={'順打逆打（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        {' '}
                        工法:
                      </span>
                      <Input
                        disabled={buildingInfoDisabledState.specialConstructionMethodStrDisabled}
                        onChange={handleSpecialConstructionMethodCd}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '40%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispSpecialConstructionMethod ?? ''}
                        title={'工法'}
                        placeholder={'工法'}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={1} colSpan={3} row={6}>
                      <FiscalMonthDropDownList
                        disabled={buildingInfoDisabledState.startYMDisabled}
                        onYearChange={handleStartY}
                        onMonthChange={handleStartM}
                        fyddlStyle={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '12%',
                          height: '80%'
                        }}
                        fmddlStyle={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '12%',
                          height: '80%'
                        }}
                        fyddlValue={
                          buildingInfoPut.startY !== undefined &&
                          buildingInfoPut.startY !== ''
                            ? Number(buildingInfoPut.startY)
                            : undefined
                        }
                        fmddlValue={
                          buildingInfoPut.startM !== undefined &&
                          buildingInfoPut.startM !== ''
                            ? Number(buildingInfoPut.startM)
                            : undefined
                        }
                        origin={-10}
                        range={21}
                        noDiv
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        fyTitle={'着工年（入力必須）'}
                        fmTitle={'着工月（入力必須）'}
                        className={'pcy-disabled-field'}
                        fmClassName={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        -
                      </span>
                      <FiscalMonthDropDownList
                        disabled={buildingInfoDisabledState.completionYMDisabled}
                        onYearChange={handleCompletionY}
                        onMonthChange={handleCompletionM}
                        fyddlStyle={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '12%',
                          height: '80%'
                        }}
                        fmddlStyle={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '12%',
                          height: '80%'
                        }}
                        fyddlValue={
                          buildingInfoPut.completionY !== undefined &&
                          buildingInfoPut.completionY !== ''
                            ? Number(buildingInfoPut.completionY)
                            : undefined
                        }
                        fmddlValue={
                          buildingInfoPut.completionM !== undefined &&
                          buildingInfoPut.completionM !== ''
                            ? Number(buildingInfoPut.completionM)
                            : undefined
                        }
                        origin={-10}
                        range={31}
                        noDiv
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        fyTitle={'竣工年（入力必須）'}
                        fmTitle={'竣工月（入力必須）'}
                        className={'pcy-disabled-field'}
                        fmClassName={'pcy-disabled-field'}
                      />
                      <NumericTextBox
                        disabled={buildingInfoDisabledState.scheduleDisabled}
                        onChange={handleSchedule}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '5%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispSchedule}
                        spinners={false}
                        title={'工期（入力必須）'}
                        placeholder={'工期（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        ヶ月
                      </span>
                      <span style={{ marginLeft: '5px' }}></span>
                      <NumericTextBox
                        disabled={buildingInfoDisabledState.siteAreaM2Disabled}
                        onChange={(e) => handleDispSiteAreaM2AndTsubo(e)}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '10%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispSiteAreaM2}
                        spinners={false}
                        title={'敷地面積平米'}
                        placeholder={'敷地面積平米'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        ㎡ /
                      </span>
                      <NumericTextBox
                        readOnly
                        disabled={true}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '10%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispSiteAreaTsubo}
                        spinners={false}
                        title={'敷地面積坪'}
                        placeholder={'敷地面積坪'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        坪
                      </span>
                      <span style={{ marginLeft: '5px' }}></span>
                      <NumericTextBox
                        disabled={buildingInfoDisabledState.sagyosyoCdDisabled}
                        onChange={handleSagyosyoCd}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '11%',
                          height: '80%'
                        }}
                        value={
                          buildingInfoPut.dispSagyosyoCd !== null
                            ? Number(buildingInfoPut.dispSagyosyoCd)
                            : null
                        }
                        max={999999}
                        format={'#'}
                        spinners={false}
                        title={'作業所コード'}
                        placeholder={'作業所コード'}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={1} row={7}>
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        備考:
                      </span>
                      <TextArea
                        disabled={buildingInfoDisabledState.remarkDisabled}
                        onChange={handleRemark}
                        value={buildingInfoPut.remark ?? ''}
                      />
                    </GridLayoutItem>
                  </GridLayout>
                </GridLayoutItem>

                <GridLayoutItem col={2}>
                  <GridLayout
                    gap={{ cols: 1 }}
                    cols={[{ width: '25%' }, { width: '75%' }]}
                  >
                    <GridLayoutItem col={1} row={1}>
                      <span style={{ fontSize: '0.8em' }}>工事部</span>
                    </GridLayoutItem>
                    <GridLayoutItem col={2} row={1}>
                      <Input
                        disabled={ buildingInfoDisabledState.constructionSectionCdDisabled}
                        onChange={handleConstructionSectionNm}
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.8em',
                          width: '54%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispConstructionSectionNm ?? ''}
                        type={'text'}
                        title={'工事部名'}
                        placeholder={'工事部名'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontSize: '0.8em' }}>）</span>
                      <Input
                        disabled={buildingInfoDisabledState.chiefPersonCdDisabled}
                        onChange={handleChiefPersonNm}
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.8em',
                          width: '40%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispChiefPersonNm ?? ''}
                        type={'text'}
                        title={'所長担当者名'}
                        placeholder={'所長担当者名'}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={2} row={2}>
                      <Input
                        disabled={buildingInfoDisabledState.chiefPersonContactsDisabled}
                        onChange={handleChiefPersonContacts}
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.8em',
                          width: '100%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispChiefPersonContacts ?? ''}
                        type={'text'}
                        title={'所長担当者連絡先'}
                        placeholder={'所長担当者連絡先'}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={1} row={3}>
                      <span style={{ fontSize: '0.8em' }}>本社営業部</span>
                    </GridLayoutItem>
                    <GridLayoutItem col={2} row={3}>
                      <Input
                        disabled={buildingInfoDisabledState.hqSalesSectionCdDisabled}
                        onChange={handleHqSalesSectionNm}
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.8em',
                          width: '54%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispHqSalesSectionNm ?? ''}
                        type={'text'}
                        title={'本社営業部名'}
                        placeholder={'本社営業部名'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontSize: '0.8em' }}>）</span>
                      <Input
                        disabled={buildingInfoDisabledState.hqSalesPersonCdDisabled}
                        onChange={handleHqSalesPersonNm}
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.8em',
                          width: '40%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispHqSalesPersonNm ?? ''}
                        type={'text'}
                        title={'本社営業担当者名'}
                        placeholder={'本社営業担当者名'}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={2} row={4}>
                      <Input
                        disabled={buildingInfoDisabledState.hqSalesPersonContactsDisabled}
                        onChange={handleHqSalesPersonContacts}
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.8em',
                          width: '100%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispHqSalesPersonContacts ?? ''}
                        type={'text'}
                        title={'本社営業担当者連絡先'}
                        placeholder={'本社営業担当者連絡先'}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={1} row={5}>
                      <span style={{ fontSize: '0.8em' }}>支店営業部</span>
                    </GridLayoutItem>
                    <GridLayoutItem col={2} row={5}>
                      <Input
                        disabled={buildingInfoDisabledState.branchSalesSectionCdDisabled}
                        onChange={handleBranchSalesSectionNm}
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.8em',
                          width: '54%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispBranchSalesSectionNm ?? ''}
                        type={'text'}
                        title={'支店営業部名'}
                        placeholder={'支店営業部名'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontSize: '0.8em' }}>）</span>
                      <Input
                        disabled={buildingInfoDisabledState.branchSalesPersonCdDisabled}
                        onChange={handleBranchSalesPersonNm}
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.8em',
                          width: '40%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispBranchSalesPersonNm ?? ''}
                        type={'text'}
                        title={'支店営業担当者名'}
                        placeholder={'支店営業担当者名'}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={2} row={6}>
                      <Input
                        disabled={buildingInfoDisabledState.branchSalesPersonContactsDisabled}
                        onChange={handleBranchSalesPersonContacts}
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.8em',
                          width: '100%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispBranchSalesPersonContacts ?? ''}
                        type={'text'}
                        title={'支店営業担当者連絡先'}
                        placeholder={'支店営業担当者連絡先'}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={1} row={7}>
                      <span style={{ fontSize: '0.8em' }}>発注者</span>
                    </GridLayoutItem>
                    <GridLayoutItem col={2} row={7}>
                      <Input
                        disabled={buildingInfoDisabledState.customerNmDisabled}
                        onChange={handleCustomerNm}
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.8em',
                          width: '50%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispCustomerNm ?? ''}
                        type={'text'}
                        title={'発注者名'}
                        placeholder={'発注者名'}
                        className={'pcy-disabled-field'}
                      />
                      <Input
                        disabled={buildingInfoDisabledState.collabCustomerNmDisabled}
                        onChange={handleCollabCustomerNm}
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.8em',
                          width: '50%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispCollabCustomerNm ?? ''}
                        type={'text'}
                        title={'共同発注者名'}
                        placeholder={'共同発注者名'}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={1} row={8}>
                      <span style={{ fontSize: '0.8em' }}>設計</span>
                    </GridLayoutItem>
                    <GridLayoutItem col={2} row={8}>
                      <DesignClassificationSelect
                        disabled={buildingInfoDisabledState.designKbnNmCdDisabled}
                        onChange={handleDesignKbnNm}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '100%',
                          height: '80%'
                        }}
                        value={[buildingInfoPut.dispDesignKbn ?? '']}
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'設計区分'}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={2} row={9}>
                      <Input
                        disabled={buildingInfoDisabledState.designerNmDisabled}
                        onChange={handleInputDesignerNm}
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.8em',
                          width: '50%',
                          height: '80%'
                        }}
                        value={buildingInfoPut.dispDesignerNm ?? ''}
                        type={'text'}
                        title={'設計者名'}
                        placeholder={'設計者名'}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                  </GridLayout>
                </GridLayoutItem>
              </GridLayout>
            </CardBody>
          </Card>
          <GridLayout style={{ marginTop: '2%' }}>
            <GridLayoutItem>
              <span style={{ fontWeight: 'bold' }}>修正コメント</span>
              <TextArea
                disabled={buildingInfoDisabledState.fixCommentDisabled}
                onChange={handleFixComment}
                value={buildingInfoPut.fixComment ?? ''}
              ></TextArea>
            </GridLayoutItem>
          </GridLayout>
        </Tooltip>
      </>
    </Contents>
  )
}
